import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import api from '../lib/api';

const TYPE_ACCENT = {
  join_request: 'oklch(0.72 0.12 82)',
  join_approved: PS.forest,
  join_rejected: PS.danger,
  round_result: PS.terracotta,
  draw_result: PS.terracotta,
  member_exited: PS.warn,
  dispute_opened: PS.danger,
};

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'เมื่อสักครู่';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} นาทีที่แล้ว`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ชม.ที่แล้ว`;
  const d = Math.floor(h / 24);
  return `${d} วันที่แล้ว`;
}

export default function NotificationBell({ color = PS.ink }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setItems(res.data.items || []);
      setUnread(res.data.unread || 0);
    } catch { /* guest / offline — ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      try { await api.post('/notifications/read'); } catch { /* noop */ }
    }
  };

  const openItem = (it) => {
    setOpen(false);
    if (it.link) nav(it.link);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button data-testid="notif-bell-btn" onClick={toggle} className="ps-press" aria-label="การแจ้งเตือน"
        style={{ position: 'relative', background: 'transparent', border: 'none', padding: 4, display: 'flex', cursor: 'pointer' }}>
        <Icon.bell size={20} stroke={color} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" style={{
            position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 999, background: PS.terracotta, color: '#fff', fontSize: 9.5, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
            fontFamily: PS.fontUI, border: `1.5px solid ${PS.page}`,
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="ps-fade" style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 320, maxWidth: '86vw',
          maxHeight: 440, overflow: 'auto', background: PS.surface2, border: `1px solid ${PS.line}`,
          borderRadius: 16, boxShadow: '0 16px 48px rgba(32,28,24,0.18)', zIndex: 200,
        }}>
          <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', position: 'sticky', top: 0, background: PS.surface2, borderBottom: `1px solid ${PS.lineSoft}` }}>
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2 }}>การแจ้งเตือน</div>
            <div style={{ fontSize: 11, color: PS.textMuted }}>{items.length} รายการ</div>
          </div>
          {items.length === 0 ? (
            <div data-testid="notif-empty" style={{ padding: '34px 20px', textAlign: 'center', color: PS.textMuted }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: PS.panel, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Icon.bell size={20} stroke={PS.textDim} />
              </div>
              <div style={{ fontSize: 13 }}>ยังไม่มีการแจ้งเตือน</div>
              <div style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>เมื่อมีคำขอเข้าวง ผลเปีย หรือการอนุมัติ เราจะแจ้งที่นี่</div>
            </div>
          ) : (
            items.map((it, i) => (
              <div key={it.notif_id} data-testid={`notif-item-${i}`} onClick={() => openItem(it)} className="ps-press"
                style={{ display: 'flex', gap: 11, padding: '12px 16px', borderBottom: i < items.length - 1 ? `1px solid ${PS.lineSoft}` : 'none', background: it.read ? 'transparent' : 'oklch(0.62 0.12 38 / 0.05)', cursor: it.link ? 'pointer' : 'default' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: TYPE_ACCENT[it.type] || PS.ink, opacity: it.read ? 0.35 : 1 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: it.read ? 500 : 700, letterSpacing: -0.1 }}>{it.title}</div>
                  {it.body && <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2, lineHeight: 1.45 }}>{it.body}</div>}
                  <div style={{ fontSize: 10.5, color: PS.textDim, marginTop: 4 }}>{timeAgo(it.created_at)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
